/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { cn } from '@/lib/utils';

export type DefaultSettingsTheme = 'light' | 'dark';

/**
 * Value shape emitted by the `inline.defaultSettings` custom editor
 * (cartridges/bm_app_storefrontnext_smg/.../editors/inline/defaultSettings.js): a theme color
 * (light/dark only), an extra CSS class, and an element id — applied to a component's outermost
 * element via {@link defaultSettingsProps}.
 */
export interface DefaultSettingsValue {
    themeColor?: string;
    itemClass?: string;
    itemID?: string;
    version?: number;
    versionName?: string;
}

const THEMES: readonly DefaultSettingsTheme[] = ['light', 'dark'];

/** Props to spread on a component's outermost element. */
export interface DefaultSettingsProps {
    id?: string;
    className: string;
}

/**
 * Build the props to spread on a component's **outermost** element from a Default Settings editor
 * value: the theme class (`bm-theme-light` | `bm-theme-dark`, which set background + text from the
 * brand tokens), the merchandiser's extra CSS class, and the optional element id. An unknown or
 * missing theme falls back to `light`.
 *
 * @example
 *   <section {...defaultSettingsProps(settings, 'relative w-full')}>
 */
export function defaultSettingsProps(value?: DefaultSettingsValue, baseClassName?: string): DefaultSettingsProps {
    const theme: DefaultSettingsTheme = THEMES.includes(value?.themeColor as DefaultSettingsTheme)
        ? (value?.themeColor as DefaultSettingsTheme)
        : 'light';
    const id = value?.itemID?.trim();
    const itemClass = value?.itemClass?.trim();
    return {
        ...(id ? { id } : {}),
        className: cn(baseClassName, `bm-theme-${theme}`, itemClass || undefined),
    };
}
